import {
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  Box,
  Skeleton,
} from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import TodayIcon from "@mui/icons-material/Today";
import MessageIcon from "@mui/icons-material/Message";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import useApiEp from "../hooks/useApiEp";
import { hexToRgba } from "./DashboardLayout";
import { useAccount } from "../context/AccountContext";

const colors = {
  primary: "#4f46e5",
  success: "#10b981",
  info: "#06b6d4",
  warning: "#f59e0b",
  textPrimary: "#111827",
};

// Updated config to support secondary (subtext) data
const dashboardItems = [
  {
    label: "Total Campaigns",
    source: "campaign",
    valueKey: "totalCampaigns",
    secondaryLabel: "Total messages sent", // Added for subtext
    secondaryValueKey: "totalMessagesSent", // Added for subtext
    fallback: "...",
    icon: <CampaignIcon />,
    color: "primary",
    gradient: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
  },
  {
    label: "Today's Campaigns",
    source: "campaign",
    valueKey: "todaysCampaigns",
    fallback: "...",
    icon: <TodayIcon />,
    color: "success",
    gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
  },
  {
    label: "Messages to unique phones (last 7 days)",
    source: "stats7Days",
    valueKey: "totalCount",
    fallback: "0",
    icon: <MessageIcon />,
    color: "info",
    gradient: "linear-gradient(135deg, #06b6d4 0%, #0ea5e9 100%)",
  },
  {
    label: "Messages to unique phones (last 24 hours)",
    source: "stats24Hours",
    valueKey: "totalCount",
    fallback: "0",
    icon: <AccessTimeIcon />,
    color: "warning",
    gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
  },
];

export default function ListDataItems() {
  const { selectedAccount } = useAccount();

  const { data: CampaignData, isLoading: isCampaignLoading } = useApiEp(
    `/campaign/get-campaign-stats?cid=${selectedAccount}`
  );

  const { data: Stats7DaysData, isLoading: isStats7DaysLoading } = useApiEp(
    `/message/last-7-days-total-count?cid=${selectedAccount}`
  );

  const { data: Stats24HoursData, isLoading: isStats24HoursLoading } = useApiEp(
    `/message/last-24-hours-total-count?cid=${selectedAccount}`
  );

  return (
    <List sx={{ flexGrow: 1, p: 0 }}>
      {dashboardItems.map((item, index) => {
        let dataset, isLoading;

        if (item.source === "campaign") {
          dataset = CampaignData;
          isLoading = isCampaignLoading;
        } else if (item.source === "stats7Days") {
          dataset = Stats7DaysData;
          isLoading = isStats7DaysLoading;
        } else if (item.source === "stats24Hours") {
          dataset = Stats24HoursData;
          isLoading = isStats24HoursLoading;
        }

        const value = dataset?.[item.valueKey] ?? item.fallback;

        // Extract secondary value if the key exists in the config
        const secondaryValue = item.secondaryValueKey
          ? dataset?.[item.secondaryValueKey] ?? item.fallback
          : null;

        return (
          <ListItem
            key={index}
            sx={{
              borderRadius: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              "&:hover": {
                backgroundColor: hexToRgba(colors[item.color], 0.05),
                transform: "translateY(-1px)",
                transition: "all 0.2s ease",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <ListItemAvatar>
                <Avatar
                  sx={{
                    bgcolor: hexToRgba(colors[item.color], 0.15),
                    color: colors[item.color],
                    width: 48,
                    height: 48,
                  }}
                >
                  {item.icon}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: 600,
                  color: colors.textPrimary,
                  fontSize: "1rem",
                }}
                // Inject the secondary subtext if configured
                secondary={
                  item.secondaryLabel ? (
                    isLoading ? (
                      <Skeleton variant="text" width={120} height={20} />
                    ) : (
                      `${item.secondaryLabel}: ${secondaryValue}`
                    )
                  ) : null
                }
                secondaryTypographyProps={{
                  fontSize: "0.85rem",
                  mt: 0.5,
                  color: "text.secondary",
                }}
              />
            </Box>

            {/* Main Value / Number */}
            {isLoading ? (
              <Skeleton variant="text" width={60} height={32} />
            ) : (
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: colors[item.color],
                  minWidth: 80,
                  textAlign: "right",
                  background: item.gradient,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {value}
              </Typography>
            )}
          </ListItem>
        );
      })}
    </List>
  );
}
